import React, { useState } from 'react';

interface DocumentViewerProps {
  content: string;
}

const DocumentViewer: React.FC<DocumentViewerProps> = ({ content }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  
  // [document]タグを解析
  const parseDocumentContent = (text: string) => {
    const documentRegex = /\[document\]([\s\S]*?)\[\/document\]/;
    const match = text.match(documentRegex);
    
    if (!match) {
      return { header: text, documentContent: null };
    }
    
    const beforeDocument = text.substring(0, match.index || 0);
    const documentContent = match[1];
    const afterDocument = text.substring((match.index || 0) + match[0].length);
    
    return {
      header: beforeDocument.trim(),
      documentContent: documentContent.trim(),
      footer: afterDocument.trim()
    };
  };
  
  const { header, documentContent, footer } = parseDocumentContent(content);
  
  // ドキュメントコンテンツがない場合は通常のテキストとして表示
  if (!documentContent) {
    return <div className="message-content">{content}</div>;
  }
  
  // ドキュメントのプレビュー（最初の数行のみ表示）
  const getPreview = (text: string, lines: number = 5) => {
    const allLines = text.split('\n');
    const previewLines = allLines.slice(0, lines);
    const hasMore = allLines.length > lines;
    
    return {
      preview: previewLines.join('\n'),
      hasMore,
      totalLines: allLines.length
    };
  };
  
  const { preview, hasMore, totalLines } = getPreview(documentContent);
  
  return (
    <div className="my-[10px]">
      {header && (
        <div className="font-medium mb-2 text-[#333]">
          {header}
        </div>
      )}

      <div className="bg-[#f8f9fa] border border-[#dee2e6] rounded-lg overflow-hidden">
        <div className="p-0">
          <button
            className="w-full px-4 py-3 bg-transparent border-none cursor-pointer flex items-center gap-2 transition-[background-color] duration-200 ease-[ease] text-[14px] text-[#495057] text-left hover:bg-black/[0.05] focus:outline focus:outline-2 focus:outline-[#007bff] focus:outline-offset-[-2px]"
            onClick={() => setIsExpanded(!isExpanded)}
            aria-expanded={isExpanded}
          >
            <span className="text-[16px] shrink-0">
              {isExpanded ? '📂' : '📁'}
            </span>
            <span className="flex-1">
              {isExpanded ? 'ドキュメントを閉じる' : 'ドキュメントを表示'}
            </span>
            {!isExpanded && hasMore && (
              <span className="text-[#6c757d] text-[12px] ml-auto">
                ({totalLines}行)
              </span>
            )}
          </button>
        </div>

        {/* 開閉状態で排他的な完全文字列を使う（Tailwind の purge は文字列連結を追えないため） */}
        <div className={isExpanded
          ? 'max-h-[500px] overflow-x-hidden overflow-y-auto transition-[max-height] duration-300 ease-[ease-out] [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-track]:bg-[#f1f1f1] [&::-webkit-scrollbar-thumb]:bg-[#888] [&::-webkit-scrollbar-thumb]:rounded [&::-webkit-scrollbar-thumb:hover]:bg-[#555]'
          : 'max-h-0 overflow-hidden transition-[max-height] duration-300 ease-[ease-out]'}>
          {isExpanded ? (
            <pre className="m-0 p-4 font-['Courier_New',Courier,monospace] text-[13px] leading-[1.5] whitespace-pre-wrap [word-break:break-word] text-[#212529] bg-white">
              {documentContent}
            </pre>
          ) : (
            <>
              <pre className="m-0 p-4 font-['Courier_New',Courier,monospace] text-[13px] leading-[1.5] whitespace-pre-wrap [word-break:break-word] text-[#212529] bg-white">
                {preview}
              </pre>
              {hasMore && (
                <div className="px-4 py-2 bg-[#f8f9fa] border-t border-t-[#dee2e6] text-[#6c757d] text-[12px] italic">
                  ... 他 {totalLines - 5} 行
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {footer && (
        <div className="mt-2 text-[#666]">
          {footer}
        </div>
      )}
    </div>
  );
};

export default DocumentViewer;